import { createUseStyles } from "react-jss";

const FIELD_COLORS = {
  light: {
    charSimp: { bg: "#FAECE7", text: "#993C1D" },
    charTrad: { bg: "#E6F1FB", text: "#185FA5" },
    pinyin: { bg: "#E1F5EE", text: "#0F6E56" },
    meaning: { bg: "#FAEEDA", text: "#854F0B" }
  },
  dark: {
    charSimp: { bg: "#4A1B0C", text: "#F0997B" },
    charTrad: { bg: "#042C53", text: "#85B7EB" },
    pinyin: { bg: "#04342C", text: "#5DCAA5" },
    meaning: { bg: "#412402", text: "#FAC775" }
  }
} as const;

type FieldKey = keyof typeof FIELD_COLORS.light;

const useStyles = createUseStyles({
  innerCard: {
    WebkitBackfaceVisibility: "hidden",
    backfaceVisibility: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "10px",
    padding: "12px",
    width: "100%",
    height: "100%",
    minHeight: 140,
    boxSizing: "border-box",
    justifyContent: "flex-start",
    borderRadius: 11,
    overflow: "hidden"
  },
  front: {
    gridRowStart: 1,
    gridColumnStart: 1,
    backgroundColor: "#2c3139",
    color: "#e2e8f0"
  },
  back: {
    transform: "rotateY(180deg)",
    gridRowStart: 1,
    gridColumnStart: 1,
    backgroundColor: "#4a627a",
    color: "#f1f5f9"
  },
  fieldRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    width: "100%",
    gap: "6px"
  },
  pill: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    minWidth: 0,
    borderRadius: 8,
    margin: 0,
    boxSizing: "border-box",
    textAlign: "center",
    overflow: "hidden"
  },
  charPill: {
    fontSize: "26px",
    fontWeight: 500,
    padding: "8px 12px",
    lineHeight: 1.2
  },
  charPillMobile: {
    fontSize: "44px",
    padding: "12px 16px"
  },
  pinyinPill: {
    fontSize: "15px",
    fontWeight: 500,
    padding: "7px 12px",
    lineHeight: 1.2
  },
  pinyinPillMobile: {
    fontSize: "20px",
    padding: "10px 14px"
  },
  meaningPill: {
    fontSize: "13px",
    fontWeight: 400,
    padding: "8px 12px",
    lineHeight: 1.5,
    wordBreak: "break-word"
  },
  meaningPillMobile: {
    fontSize: "16px",
    padding: "10px 14px"
  },
  innerCardMobile: {
    minHeight: "52vh",
    justifyContent: "center"
  },
  innerCardCompact: {
    minHeight: 96,
    padding: "8px",
    gap: "6px",
    justifyContent: "flex-start"
  },
  charPillCompact: {
    fontSize: "22px",
    padding: "4px 8px"
  },
  pinyinPillCompact: {
    fontSize: "13px",
    padding: "4px 8px"
  },
  meaningPillCompact: {
    fontSize: "11px",
    padding: "4px 8px",
    lineHeight: 1.3
  },
  innerCardEmbedded: {
    backgroundColor: "transparent",
    borderRadius: 0,
    minHeight: 0,
    height: "auto",
    flex: 1
  }
});

const getColorMode = (currentSide: string) => (currentSide === "front" ? "light" : "dark");

const getPillColors = (field: FieldKey, currentSide: string) => {
  const mode = getColorMode(currentSide);
  return FIELD_COLORS[mode][field];
};

const hasFieldContent = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const CardDetails = ({
  cardDetails,
  currentSide,
  detailsOptions,
  isMobile = false,
  isCompact = false,
  embedded = false
}: {
  cardDetails: any;
  currentSide: string;
  detailsOptions: any;
  isMobile?: boolean;
  isCompact?: boolean;
  embedded?: boolean;
}) => {
  const currentSideDetailsOptions = detailsOptions[currentSide];
  const currentSideDetailsOptionsKeys = Object.keys(currentSideDetailsOptions);
  const detailsToShow = currentSideDetailsOptionsKeys.filter(
    (key) =>
      currentSideDetailsOptions[key] &&
      key !== "charSimp" &&
      key !== "charTrad" &&
      hasFieldContent(cardDetails[key])
  );
  const charDetailsToShow = currentSideDetailsOptionsKeys.filter(
    (key) => currentSideDetailsOptions[key] && (key === "charSimp" || key === "charTrad")
  );

  const shouldShowCharSimp = charDetailsToShow.includes("charSimp");
  const shouldShowCharTrad = charDetailsToShow.includes("charTrad");
  const charSimp = cardDetails["charSimp"];
  const charTrad = cardDetails["charTrad"];
  const hasCharSimp = shouldShowCharSimp && hasFieldContent(charSimp);
  const hasCharTrad = shouldShowCharTrad && hasFieldContent(charTrad);
  const simpAndTradTheSame = hasCharSimp && hasCharTrad && charSimp === charTrad;
  const areBothSelected = shouldShowCharSimp && shouldShowCharTrad;
  const showBoth = !simpAndTradTheSame && hasCharSimp && hasCharTrad;
  const classes = useStyles();

  const renderPill = (key: string, field: FieldKey, content: string, pillClass: string) => {
    if (!hasFieldContent(content)) {
      return null;
    }

    const colors = getPillColors(field, currentSide);
    const mobileClass =
      isCompact && field === "charSimp"
        ? classes.charPillCompact
        : isCompact && field === "charTrad"
          ? classes.charPillCompact
          : isCompact && field === "pinyin"
            ? classes.pinyinPillCompact
            : isCompact && field === "meaning"
              ? classes.meaningPillCompact
              : isMobile && field === "charSimp"
                ? classes.charPillMobile
                : isMobile && field === "charTrad"
                  ? classes.charPillMobile
                  : isMobile && field === "pinyin"
                    ? classes.pinyinPillMobile
                    : isMobile && field === "meaning"
                      ? classes.meaningPillMobile
                      : "";

    return (
      <span
        key={key}
        className={`${classes.pill} ${pillClass} ${mobileClass}`}
        style={{ backgroundColor: colors.bg, color: colors.text }}>
        {content}
      </span>
    );
  };

  return (
    <div
      className={`${classes.innerCard} ${classes[currentSide]} ${
        embedded
          ? classes.innerCardEmbedded
          : isCompact
            ? classes.innerCardCompact
            : isMobile
              ? classes.innerCardMobile
              : ""
      }`}>
      {hasCharSimp || hasCharTrad ? (
        <div className={classes.fieldRow}>
          {hasCharSimp && renderPill("charSimp", "charSimp", charSimp, classes.charPill)}
          {(showBoth || (hasCharTrad && !areBothSelected)) &&
            renderPill("charTrad", "charTrad", charTrad, classes.charPill)}
        </div>
      ) : null}
      {detailsToShow.map((cardDetail) => {
        if (cardDetail === "pinyin") {
          return (
            <div key={cardDetail} className={classes.fieldRow}>
              {renderPill(cardDetail, "pinyin", cardDetails[cardDetail], classes.pinyinPill)}
            </div>
          );
        }
        if (cardDetail === "meaning") {
          return (
            <div key={cardDetail} className={classes.fieldRow}>
              {renderPill(cardDetail, "meaning", cardDetails[cardDetail], classes.meaningPill)}
            </div>
          );
        }
        return hasFieldContent(cardDetails[cardDetail]) ? (
          <div key={cardDetail} className={classes.fieldRow}>
            <span className={classes.pill}>{cardDetails[cardDetail]}</span>
          </div>
        ) : null;
      })}
    </div>
  );
};

export default CardDetails;
